import type { PaletteMode } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { ShadesColor } from '@/mui/types';

import { ALPHA_300, ALPHA_500, ALPHA_700, ALPHA_800 } from '../../opacity';
import { ACTIONS_OPACITY } from '../index';

// export const TAG_PEOPLE = '#1A8236';
// export const TAG_BRANDS = '#1C5F99';
// export const TAG_IAB = '#8E2CA7';
// export const TAG_BRAND_SAFETY = '#B63104';
// export const TAG_KEYWORDS = '#155766';
// export const TAG_TEXT = '#785020';
// export const TAG_CC = '#5B2CC4';

export const TAG_PEOPLE = {
  dark: '#BDFDD7',
  main: '#DAFDE8',
  light: '#E8FFF1',
  contrastText: '#1A8236',
};

export const TAG_BRANDS = {
  dark: '#CCDAF0',
  main: '#DBE8FB',
  light: '#E7F0FF',
  contrastText: '#1C5F99',
};

export const TAG_IAB = {
  dark: '#F3C5FF',
  main: '#F8DBFF',
  light: '#FBE9FF',
  contrastText: '#8E2CA7',
};

export const TAG_BRAND_SAFETY = {
  dark: '#FFCCBB',
  main: '#FFDFD3',
  light: '#FFEAE2',
  contrastText: '#B63104',
};

export const TAG_KEYWORDS = {
  dark: '#ABF0FF',
  main: '#C7F5FF',
  light: '#D9F8FF',
  contrastText: '#155766',
};

export const TAG_TEXT = {
  dark: '#FFDAAE',
  main: '#FFE6C9',
  light: '#FFEDD9',
  contrastText: '#785020',
};

export const TAG_CC = {
  dark: '#D4C2FE',
  main: '#E2D7FF',
  light: '#EBE3FF',
  contrastText: '#5B2CC4',
};

const SHADES: ShadesColor = {
  0: '#161C24',
  50: '#161C24',
  100: '#212B36',
  200: '#454F5B',
  300: '#637381',
  400: '#919EAB',
  500: '#C4CDD5',
  600: '#DFE3E8',
  700: '#F4F6F8',
  800: '#F9FAFB',
  900: '#FFFFFF',
  A100: '#FFFFFF',
  A200: '#FFFFFF',
  A400: '#FFFFFF',
  A700: '#FFFFFF',
};

const PRIMARY = {
  dark: '#497AF7',
  main: '#6E9EFA',
  light: '#9FC0FF',
  contrastText: '#000',
};

const SECONDARY = {
  dark: '#8744EF',
  main: '#9B61F3',
  light: '#B584FF',
  contrastText: '#000',
};

const ERROR = {
  dark: '#F43E37',
  main: '#F5605B',
  light: '#FF7772',
  contrastText: '#000',
};

const WARNING = {
  dark: '#FFB800',
  main: '#FFCA42',
  light: '#FFD873',
  contrastText: '#000',
};

const HIGHLIGHT = {
  dark: '#ffee58',
  main: '#fff176',
  light: '#fff59d',
  contrastText: '#000',
};

const HIGHLIGHT_2 = {
  dark: '#0293EA',
  main: '#00A0FF',
  light: '#32B3FF',
  contrastText: '#fff',
};

const COMPLIMENTARY = {
  dark: '#37C7B9',
  main: '#4CDDCF',
  light: '#75FBEB',
  contrastText: '#000',
};

const SUCCESS = {
  dark: '#6EE625',
  main: '#89FD42',
  light: '#B8FF8D',
  contrastText: '#000',
};

const INFO = {
  dark: '#7E8AB7',
  main: '#929EC8',
  light: '#ACB9DA',
  contrastText: '#000',
};

const BACKGROUND = {
  paper: '#2b343f',
  default: '#21262c',
};

const TEXT = {
  primary: alpha('#fff', ALPHA_800),
  secondary: alpha('#fff', ALPHA_700),
  disabled: alpha('#fff', ALPHA_500),
};

const GRAPHS = [
  '#7439D4', // 0
  '#C05BA4', // 1
  '#20914D', // 2
  '#1A658F', // 3
  '#637595', // 4
  '#71AED2', // 5
  '#8A502A', // 6
  '#E53436', // 7
  '#84EB34', // 8
  '#F0AD54', // 9
  '#9748FF', // 10
  '#FC73FF', // 11
  '#9BE757', // 12
  // Marketplace graphs
  '#2F56E7', // 13
  '#2BD7CC', // 14
  '#06A0FF', // 15
  '#FF4702', // 16
];

const GRADIENT = ['#A1B6E0'];

const PLATFORM = {
  anyclip: '#663d9e',
  youtube: '#ff0000',
  linkedin: '#2867b2',
  facebook: '#1877f2',
  twitter: '#000000',
  whatsapp: '#25d366',
  default: '#6782a4',
};

const palette = {
  mode: 'dark' as PaletteMode,
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  highlight: HIGHLIGHT,
  highlight2: HIGHLIGHT_2,
  error: ERROR,
  complimentary: COMPLIMENTARY,
  text: TEXT,

  gradient: GRADIENT,

  platform: PLATFORM,

  '-tagText': TAG_TEXT,
  '-tagBrandSafety': TAG_BRAND_SAFETY,
  '-tagBrands': TAG_BRANDS,
  '-tagIAB': TAG_IAB,
  '-tagPeople': TAG_PEOPLE,
  '-tagKeywords': TAG_KEYWORDS,
  '-tagCC': TAG_CC,

  '-graph': GRAPHS,

  divider: alpha(SHADES[900], ALPHA_300),
  background: BACKGROUND,
  shades: SHADES,
  action: {
    active: SHADES[900],
    hover: alpha(SHADES[900], ACTIONS_OPACITY.hoverOpacity),
    selected: alpha(SHADES[900], ACTIONS_OPACITY.selectedOpacity),
    disabled: alpha(SHADES[900], ACTIONS_OPACITY.disabledOpacity),
    disabledBackground: alpha(SHADES[900], ACTIONS_OPACITY.disabledBackgroundOpacity),
    focus: alpha(SHADES[900], ACTIONS_OPACITY.focusOpacity),
    ...ACTIONS_OPACITY,
  },
};

export type PaletteType = typeof palette;

export default palette;
